/**
 * How far away, in which direction, and in what order.
 *
 * Computed client-side, deliberately. There is no geospatial index to query:
 * `latitude` and `longitude` are plain Numbers on both models, with no GeoJSON
 * field and no `2dsphere`, so `$near` is not available without a migration.
 * The user's own set is bounded and is already fetched for the map, so sorting
 * it here costs nothing. If a tenant ever has thousands of sites the answer is
 * that migration, not a slower client.
 */

export interface Coords {
	latitude: number
	longitude: number
}

/** A record that may or may not know where it is. */
export interface Positioned {
	latitude?: number | null
	longitude?: number | null
}

const EARTH_RADIUS_M = 6_371_000
const toRad = (deg: number): number => (deg * Math.PI) / 180
const toDeg = (rad: number): number => (rad * 180) / Math.PI

/** True when both coordinates are real numbers in range. */
export const hasCoords = (record: Positioned | null | undefined): boolean => {
	if (!record) return false
	const { latitude: lat, longitude: lng } = record
	return (
		typeof lat === 'number' &&
		typeof lng === 'number' &&
		Number.isFinite(lat) &&
		Number.isFinite(lng) &&
		Math.abs(lat) <= 90 &&
		Math.abs(lng) <= 180 &&
		// 0,0 is in the Gulf of Guinea. Nothing in this app is there, and it is
		// what an unset pair of fields looks like after a bad import.
		!(lat === 0 && lng === 0)
	)
}

/**
 * Great-circle distance in metres.
 *
 * Haversine rather than a projected approximation: the error of a flat-earth
 * shortcut is small at these ranges but its failure mode is silent, and this
 * is cheap enough that there is nothing to buy by being clever.
 */
export const distanceMetres = (from: Coords, to: Coords): number => {
	const dLat = toRad(to.latitude - from.latitude)
	const dLng = toRad(to.longitude - from.longitude)
	const lat1 = toRad(from.latitude)
	const lat2 = toRad(to.latitude)

	const a =
		Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)
	return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)))
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const
export type Compass = (typeof COMPASS)[number]

/**
 * Eight-point compass bearing, which is as much as anyone reads off a list.
 *
 * "0.4 mi NE" tells a driver which way to turn out of the car park; "0.4 mi,
 * bearing 037°" does not.
 */
export const bearingFrom = (from: Coords, to: Coords): Compass => {
	const lat1 = toRad(from.latitude)
	const lat2 = toRad(to.latitude)
	const dLng = toRad(to.longitude - from.longitude)

	const y = Math.sin(dLng) * Math.cos(lat2)
	const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng)
	const degrees = (toDeg(Math.atan2(y, x)) + 360) % 360

	return COMPASS[Math.round(degrees / 45) % 8]
}

const METRES_PER_MILE = 1609.344
const METRES_PER_FOOT = 0.3048

/**
 * Distance as a person would say it.
 *
 * Imperial, because this is a US commercial real estate tool and every other
 * measure in the app (`sqft`, `frontage`) already is. There is no unit
 * preference anywhere in the product to read; when one exists, this is the one
 * place to branch.
 *
 * Feet under a tenth of a mile, because "0.0 mi" is not an answer, and whole
 * miles past ten, because a tenth of a mile is noise at that range.
 */
export const formatDistance = (metres: number): string => {
	if (!Number.isFinite(metres) || metres < 0) return ''

	const miles = metres / METRES_PER_MILE
	if (miles < 0.1) return `${Math.round(metres / METRES_PER_FOOT / 10) * 10} ft`
	if (miles < 10) return `${miles.toFixed(1)} mi`
	return `${Math.round(miles)} mi`
}

export interface WithDistance<T> {
	record: T
	/** Null when the record has no usable coordinates. */
	metres: number | null
	bearing: Compass | null
}

/**
 * Records nearest-first, with the unplaceable ones last.
 *
 * Sorting a record with no coordinates as though it were at 0,0 puts it at the
 * top of the list from anywhere in the Atlantic and near the bottom from
 * California — either way it is wrong, and quietly so. Plenty of locations
 * genuinely have no position, so they get an explicit null and go to the end
 * in their original order.
 *
 * With no fix at all, everything keeps its incoming order rather than being
 * shuffled arbitrarily: an unsorted list the user can still read beats a
 * sorted-by-nothing one.
 */
export const sortByDistance = <T extends Positioned>(
	records: T[],
	from: Coords | null,
): WithDistance<T>[] => {
	const measured = records.map(record => {
		if (!from || !hasCoords(record)) return { record, metres: null, bearing: null }
		const to = { latitude: record.latitude as number, longitude: record.longitude as number }
		return { record, metres: distanceMetres(from, to), bearing: bearingFrom(from, to) }
	})

	if (!from) return measured

	// Stable: equal distances, and the whole tail of unplaceable records, keep
	// the order they arrived in.
	return measured
		.map((item, index) => ({ item, index }))
		.sort((a, b) => {
			if (a.item.metres === null && b.item.metres === null) return a.index - b.index
			if (a.item.metres === null) return 1
			if (b.item.metres === null) return -1
			return a.item.metres - b.item.metres || a.index - b.index
		})
		.map(({ item }) => item)
}
